import type { Icon as TablerIcon } from "@tabler/icons-react";
import type { Model } from "@/contexts/models";
import type { ResolvedShortId } from "@/contexts/session/data/hooks";
import { PRODUCT_TYPE_ICONS } from "@/utils/productTypeIcons";
import { NOT_STARTED_VERSION_LABEL } from "@/utils/versionPresentation";
import { getProviderIconPath } from "@/variety/llmProviders";
import { SessionEntityKind } from "@cabal/shared/domain/session/enums.js";
import {
  CREFERENCE_ENTITY_TYPE,
  type CreferenceEntityType,
} from "./CreferenceEntity";

const RESOLVED_KIND_TO_REF_TYPE: Partial<
  Record<SessionEntityKind, CreferenceEntityType>
> = {
  [SessionEntityKind.Product]: CREFERENCE_ENTITY_TYPE.PRODUCT,
  [SessionEntityKind.Version]: CREFERENCE_ENTITY_TYPE.VERSION,
  [SessionEntityKind.File]: CREFERENCE_ENTITY_TYPE.FILE,
  [SessionEntityKind.Member]: CREFERENCE_ENTITY_TYPE.MEMBER,
  [SessionEntityKind.Collab]: CREFERENCE_ENTITY_TYPE.COLLAB,
  [SessionEntityKind.User]: CREFERENCE_ENTITY_TYPE.USER,
};

const UNKNOWN_LABEL = "[unknown]";
const LOADING_LABEL = "[loading]";
const USER_PLACEHOLDER_IMAGE = "/profile image placeholder.png";
const EMPTY_RESOLVED_ENTITIES: CreferenceResolvedEntities = {
  collab: null,
  file: null,
  member: null,
  product: null,
  resolvedUserId: null,
  version: null,
};

export type ReferenceProduct = ResolvedShortId["product"];
export type ReferenceVersion = ResolvedShortId["version"];
export type ReferenceFile = ResolvedShortId["file"];
export type ReferenceMember = ResolvedShortId["member"];
export type ReferenceCollab = ResolvedShortId["collab"];

interface CreferenceResolvedEntities {
  collab: ReferenceCollab | null;
  file: ReferenceFile | null;
  member: ReferenceMember | null;
  product: ReferenceProduct | null;
  resolvedUserId: string | null;
  version: ReferenceVersion | null;
}

interface CreferenceModelLookup {
  getModel: (id: string) => Model | undefined;
  getUniqueVendors: (modelIdentifiers: string[]) => string[];
}

interface ResolveMemberProviderSlugInput extends CreferenceModelLookup {
  member: ReferenceMember | null;
}

interface SelectKnownProviderSlugInput {
  getModel: (id: string) => Model | undefined;
  modelId: string;
}

interface SelectFallbackProviderSlugInput {
  getUniqueVendors: (modelIdentifiers: string[]) => string[];
  modelId: string;
}

interface ResolveCurrentUserReferenceInput {
  resolved: ResolvedShortId | null;
  sessionUserId: string | null;
}

interface ResolveCurrentUserFieldInput {
  isCurrentUserReference: boolean;
}

interface ResolveCurrentUserNameInput extends ResolveCurrentUserFieldInput {
  chatName: string | null;
}

interface ResolveCurrentUserImageInput extends ResolveCurrentUserFieldInput {
  imageUrl: string | null;
}

interface SelectCreferenceIsLoadingInput {
  entityType: CreferenceEntityType;
  isCurrentUserReference: boolean;
  profileLoading: boolean;
  sessionLoading: boolean;
}

interface SelectCreferenceLabelInput {
  collab: ReferenceCollab | null;
  currentUserName: string | null;
  file: ReferenceFile | null;
  entityType: CreferenceEntityType;
  member: ReferenceMember | null;
  product: ReferenceProduct | null;
  version: ReferenceVersion | null;
  /** The product a version reference belongs to — the fallback name for a
   *  version nobody has written yet. Null for every other entity type. */
  versionProduct: ReferenceProduct | null;
}

/**
 * A label and the marker that must OUTLIVE truncation. An unwritten version
 * borrows its product's name, so the version number is the only thing telling
 * two of them apart — cutting it off with the rest of the name would leave the
 * pill claiming to be the product itself.
 */
export interface CreferenceLabelParts {
  label: string | null;
  suffix: string | null;
}

interface SelectVersionLabelPartsInput {
  version: NonNullable<ReferenceVersion>;
  versionProduct: ReferenceProduct | null;
}

interface ResolveCreferenceLabelStateInput {
  isLoading: boolean;
  resolvedParts: CreferenceLabelParts;
}

interface CreferenceLabelState {
  isUnresolved: boolean;
  label: string;
  labelSuffix: string | null;
}

type CreferenceLoadingSelector = (
  input: SelectCreferenceIsLoadingInput,
) => boolean;

type CreferenceLabelSelector = (
  input: SelectCreferenceLabelInput,
) => CreferenceLabelParts;

/** Every entity in a reference comes out of the session payload, so while that
 *  payload is in flight NONE of them can be resolved. Reporting only the user
 *  type as loading made every other pill render `[unknown]` mid-load — a
 *  permanent-looking dead end for a name that was seconds from arriving. */
function isSessionLoading(input: SelectCreferenceIsLoadingInput): boolean {
  return input.sessionLoading;
}

const CREFERENCE_LOADING_SELECTOR_BY_TYPE: Record<
  CreferenceEntityType,
  CreferenceLoadingSelector
> = {
  [CREFERENCE_ENTITY_TYPE.PRODUCT]: isSessionLoading,
  [CREFERENCE_ENTITY_TYPE.VERSION]: isSessionLoading,
  [CREFERENCE_ENTITY_TYPE.FILE]: isSessionLoading,
  [CREFERENCE_ENTITY_TYPE.MEMBER]: isSessionLoading,
  [CREFERENCE_ENTITY_TYPE.COLLAB]: isSessionLoading,
  // The user reference waits on a second request: the session tells us WHICH
  // user, the profile tells us their name.
  [CREFERENCE_ENTITY_TYPE.USER]: (input) =>
    isSessionLoading(input)
    || (input.isCurrentUserReference && input.profileLoading),
  [CREFERENCE_ENTITY_TYPE.UNKNOWN]: isSessionLoading,
};

const CREFERENCE_LABEL_SELECTOR_BY_TYPE: Record<
  CreferenceEntityType,
  CreferenceLabelSelector
> = {
  [CREFERENCE_ENTITY_TYPE.PRODUCT]: (input) =>
    labelWithoutSuffix(input.product?.name ?? null),
  [CREFERENCE_ENTITY_TYPE.VERSION]: (input) =>
    input.version
      ? selectVersionLabelParts({
        version: input.version,
        versionProduct: input.versionProduct,
      })
      : labelWithoutSuffix(null),
  [CREFERENCE_ENTITY_TYPE.MEMBER]: (input) =>
    labelWithoutSuffix(input.member?.name ?? null),
  [CREFERENCE_ENTITY_TYPE.FILE]: (input) =>
    labelWithoutSuffix(input.file?.displayName ?? null),
  [CREFERENCE_ENTITY_TYPE.COLLAB]: (input) =>
    labelWithoutSuffix(input.collab?.shortestSummary ?? null),
  [CREFERENCE_ENTITY_TYPE.USER]: (input) =>
    labelWithoutSuffix(input.currentUserName),
  [CREFERENCE_ENTITY_TYPE.UNKNOWN]: () => labelWithoutSuffix(null),
};

export function normalizeEntityId(entityId: string): string {
  return entityId.trim();
}

export function selectCreferenceResolvedEntities(
  resolved: ResolvedShortId | null,
): CreferenceResolvedEntities {
  if (!resolved) {
    return EMPTY_RESOLVED_ENTITIES;
  }

  const {
    collab = null,
    file = null,
    member = null,
    product = null,
    userId = null,
    version = null,
  } = resolved;

  return {
    collab,
    file,
    member,
    product,
    resolvedUserId: userId,
    version,
  };
}

export function selectCreferenceEntityType(
  resolved: ResolvedShortId | null,
): CreferenceEntityType {
  if (!resolved) {
    return CREFERENCE_ENTITY_TYPE.UNKNOWN;
  }

  return (
    RESOLVED_KIND_TO_REF_TYPE[resolved.type]
    ?? CREFERENCE_ENTITY_TYPE.UNKNOWN
  );
}

export function selectIsCurrentUserReference({
  resolved,
  sessionUserId,
}: ResolveCurrentUserReferenceInput): boolean {
  if (!sessionUserId || !resolved) {
    return false;
  }

  if (resolved.type !== SessionEntityKind.User) {
    return false;
  }

  return resolved.userId === sessionUserId;
}

export function selectCurrentUserName({
  chatName,
  isCurrentUserReference,
}: ResolveCurrentUserNameInput): string | null {
  if (!isCurrentUserReference) {
    return null;
  }

  return firstWord(chatName ?? "") || null;
}

export function selectCurrentUserImageSrc({
  imageUrl,
  isCurrentUserReference,
}: ResolveCurrentUserImageInput): string | null {
  if (!isCurrentUserReference) {
    return null;
  }

  return imageUrl || USER_PLACEHOLDER_IMAGE;
}

export function resolveMemberProviderSlug({
  getModel,
  getUniqueVendors,
  member,
}: ResolveMemberProviderSlugInput): string {
  const modelId = selectMemberModelId(member);
  if (modelId == null) {
    return "";
  }

  const knownProviderSlug = selectKnownProviderSlug({ getModel, modelId });
  if (knownProviderSlug != null) {
    return knownProviderSlug;
  }

  return selectFallbackProviderSlug({ getUniqueVendors, modelId });
}

export function resolveMemberProviderIconPath(input: {
  member: ReferenceMember | null;
  memberProviderSlug: string;
}): string {
  if (!input.member) {
    return "";
  }

  return getProviderIconPath(input.memberProviderSlug);
}

export function selectCreferenceIsLoading(
  input: SelectCreferenceIsLoadingInput,
): boolean {
  return CREFERENCE_LOADING_SELECTOR_BY_TYPE[input.entityType](input);
}

export function selectCreferenceLabelParts(
  input: SelectCreferenceLabelInput,
): CreferenceLabelParts {
  return CREFERENCE_LABEL_SELECTOR_BY_TYPE[input.entityType](input);
}

export function resolveCreferenceLabelState({
  isLoading,
  resolvedParts,
}: ResolveCreferenceLabelStateInput): CreferenceLabelState {
  const hasLabel = Boolean(resolvedParts.label);
  return {
    isUnresolved: !isLoading && !hasLabel,
    label: isLoading ? LOADING_LABEL : (resolvedParts.label ?? UNKNOWN_LABEL),
    // `[loading]` and `[unknown]` are our words, not a name — a version marker
    // hung off either would read as a real version of a real thing.
    labelSuffix: isLoading || !hasLabel ? null : resolvedParts.suffix,
  };
}

/** The label as one string, for the places that cannot keep the marker out of
 *  the truncation budget: the aria label and the nested plain-text terminator. */
export function composeCreferenceLabel(input: {
  label: string;
  labelSuffix: string | null;
}): string {
  return input.labelSuffix == null
    ? input.label
    : `${input.label} ${input.labelSuffix}`;
}

export function selectCreferenceProductIcon(
  product: ReferenceProduct | null,
): TablerIcon | null {
  return product ? PRODUCT_TYPE_ICONS[product.type] : null;
}

function firstWord(input: string): string {
  const normalized = input.trim();
  if (!normalized) {
    return "";
  }

  return normalized.split(/\s+/)[0] ?? "";
}

function selectMemberModelId(member: ReferenceMember | null): string | null {
  if (member?.modelId == null) {
    return null;
  }

  return String(member.modelId);
}

function selectKnownProviderSlug({
  getModel,
  modelId,
}: SelectKnownProviderSlugInput): string | null {
  return getModel(modelId)?.vendorSlug ?? null;
}

function selectFallbackProviderSlug({
  getUniqueVendors,
  modelId,
}: SelectFallbackProviderSlugInput): string {
  return getUniqueVendors([modelId])[0] ?? "";
}

function labelWithoutSuffix(label: string | null): CreferenceLabelParts {
  return { label, suffix: null };
}

/** Version marker for a placeholder borrowing its product's name — `V1`, `V2`. */
function versionMarker(versionNumber: number): string {
  return `V${versionNumber}`;
}

/**
 * A version nobody has written yet has no title of its own — only the `(empty)`
 * sentinel the chair's seed stamps to satisfy the column's non-empty check. So
 * it borrows the name of the product it belongs to, marked with its version
 * number. `NOT_STARTED_VERSION_LABEL` survives as the last resort for a version
 * whose product is not in the session payload at all.
 */
function selectVersionLabelParts({
  version,
  versionProduct,
}: SelectVersionLabelPartsInput): CreferenceLabelParts {
  if (!version.isSeededPlaceholder) {
    return labelWithoutSuffix(version.contentTitle);
  }

  const productName = versionProduct?.name;
  if (!productName) {
    return labelWithoutSuffix(NOT_STARTED_VERSION_LABEL);
  }

  return { label: productName, suffix: versionMarker(version.versionNumber) };
}
